import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { cultureSummary, type OrderState, type Patient, type PatientHistory } from "@probetalab/contracts";
import { get, getBlob } from "../lib/api.js";
import { patientAge, fmtDate, fmtDateNumeric, fmtDateTime, fmtNumber, fmtPhone, fmtTime, fullName, initials, sexLabel } from "../lib/format.js";
import { printPdf } from "../lib/print.js";
import { useSession } from "../lib/session.js";
import { ITEM_STATE_LABEL, itemTone, ORDER_STATE_LABEL } from "../lib/status.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useDebounced, useQuery } from "../lib/useQuery.js";
import { PatientForm, patientToForm } from "../components/PatientPicker.js";
import { Button, Card, Chip, type ChipTone, Definition, Empty, IconButton, Loading, PageHead, SearchInput, Sheet } from "../components/ui.js";
import { Icon } from "../components/icons.js";
import { RichTextView } from "../components/RichText.js";
import { RefText } from "../components/RefText.js";

export function PatientsPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const dq = useDebounced(q.trim());
  const [showNew, setShowNew] = useState(false);
  const list = useQuery(() => get("patients", { query: { q: dq || undefined, limit: 60 } }), [dq]);
  return (
    <>
      <PageHead
        title="Pacientes"
        lede="Busca por nombre o celular. El historial muestra visitas, estudios y resultados validados."
        actions={
          <Button variant="primary" size="lg" icon="userPlus" onClick={() => setShowNew(true)}>
            Registrar paciente
          </Button>
        }
      />
      <div className="toolbar">
        <SearchInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, apellido o celular…" autoFocus className="lg" aria-label="Buscar paciente" />
      </div>
      <Card pad={false}>
        {list.loading && !list.data ? (
          <Loading />
        ) : !list.data?.items.length ? (
          <Empty icon="users" title="Sin pacientes" hint={dq ? "Nadie coincide con esa búsqueda." : "Registra al primero."} />
        ) : (
          <div className="list">
            {list.data.items.map((p) => (
              <Link key={p.id} to={`/pacientes/${p.id}`} className="list-row clickable">
                <span className={`avatar ${p.sex}`}>{initials(p)}</span>
                <span className="grow">
                  <div className="primary">{fullName(p)}</div>
                  <div className="secondary">
                    {sexLabel(p.sex)} · {patientAge(p)}{p.birth_date_is_estimated ? "" : ` · nació el ${fmtDate(p.birth_date)}`}
                  </div>
                </span>
                <span className="muted tnum">{p.phone ? fmtPhone(p.phone) : ""}</span>
                <Icon name="chevronRight" style={{ color: "var(--ink-3)" }} />
              </Link>
            ))}
          </div>
        )}
      </Card>
      <Sheet open={showNew} onClose={() => setShowNew(false)} title="Registrar paciente" wide>
        <PatientForm
          onSaved={(p: Patient) => {
            setShowNew(false);
            navigate(`/pacientes/${p.id}`);
          }}
          onCancel={() => setShowNew(false)}
        />
      </Sheet>
    </>
  );
}

type HistoryOrder = PatientHistory["orders"][number];
type HistoryItem = HistoryOrder["items"][number];

function orderTone(state: string): ChipTone {
  if (state === "reported_partial" || state === "reported_final" || state === "closed") return "green";
  if (state === "in_process") return "blue";
  return "neutral";
}

/** Values leave the API only once validated (or issued); anything else reads "Pendiente". */
function isReleased(it: HistoryItem): boolean {
  return it.state === "validated" || it.state === "issued";
}

/**
 * Patient detail as master–detail, the way Toronja's "Historial del Paciente" read (pilot
 * request, 2026-11-02: "que salga la fecha y del otro lado los estudios"): the visits on the
 * left, newest first, with a print button for the visit's final report; the patient and the
 * selected visit's studies and validated results on the right. Narrow screens stack the list
 * above the detail.
 */
export function PatientPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useSession();
  const history = useQuery(() => get("patientHistory", { params: { id } }), [id]);
  const [editing, setEditing] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [printing, setPrinting] = useState<string | null>(null);
  const detailRef = useRef<HTMLDivElement>(null);

  if (history.loading && !history.data) return <Loading />;
  if (!history.data) return <Empty icon="warning" title="No se encontró al paciente" hint={history.error ?? undefined} />;
  const { patient: p, orders } = history.data;
  const selected = orders.find((o) => o.order_id === selectedId) ?? orders[0] ?? null;
  const staff = can("quimico");

  function select(orderId: string) {
    setSelectedId(orderId);
    // Stacked layout: bring the studies into view; side by side they already are.
    if (typeof window.matchMedia === "function" && window.matchMedia("(max-width: 1100px)").matches) {
      detailRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }
  }

  async function print(o: HistoryOrder) {
    const r = o.latest_report;
    if (!r) return;
    setPrinting(o.order_id);
    try {
      printPdf(await getBlob("reportPdf", { params: { id: r.id }, query: { staff_view: r.held_for_balance && staff } }));
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setPrinting(null);
    }
  }

  return (
    <>
      <PageHead
        kicker="Paciente"
        title={fullName(p)}
        lede={`${sexLabel(p.sex)} · ${patientAge(p)} · ${orders.length} ${orders.length === 1 ? "visita" : "visitas"}`}
        actions={
          <>
            <Button variant="ghost" icon="chevronLeft" onClick={() => navigate(-1)}>
              Volver
            </Button>
            <Button variant="secondary" icon="pen" onClick={() => setEditing(true)}>
              Editar datos
            </Button>
            <Button variant="primary" icon="plus" onClick={() => navigate("/recepcion", { state: { patient: p } })}>
              Nueva solicitud
            </Button>
          </>
        }
      />
      <div className="split narrow-left patient-history">
        <Card title="Historial del paciente" pad={false}>
          {orders.length === 0 ? (
            <Empty icon="history" title="Sin visitas" hint="Las solicitudes y sus resultados validados aparecerán aquí." />
          ) : (
            <ul className="list history-list" aria-label="Visitas del paciente">
              {orders.map((o) => {
                const active = o.order_id === selected?.order_id;
                const r = o.latest_report;
                const blocked = !!r && r.held_for_balance && !staff;
                return (
                  <li key={o.order_id} className={`history-row ${active ? "active" : ""}`}>
                    <button type="button" className="list-row clickable" aria-pressed={active} onClick={() => select(o.order_id)}>
                      <span className="grow">
                        <span className="folio">{o.folio}</span>
                        <span className="secondary tnum">
                          {fmtDateNumeric(o.occurred_at)} · {fmtTime(o.occurred_at)}
                        </span>
                      </span>
                      <span className="history-chips">
                        <Chip tone={orderTone(o.state)}>{ORDER_STATE_LABEL[o.state as OrderState] ?? o.state}</Chip>
                        {o.balance_cents > 0 && <Chip tone="amber">Adeudo</Chip>}
                      </span>
                    </button>
                    <span className="history-print">
                      {r && (
                        <IconButton
                          icon="print"
                          label={blocked ? `Imprimir ${o.folio}: tiene adeudo` : `Imprimir ${o.folio}`}
                          disabled={blocked || printing === o.order_id}
                          onClick={() => void print(o)}
                        />
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
        <div className="stack-lg" ref={detailRef}>
          <Card title="Datos del paciente" actions={<span className="muted small">Registrado {fmtDateTime(p.created_at)}</span>}>
            <div className="patient-facts">
              <Definition k="Nombre" v={fullName(p)} />
              <Definition k="Sexo" v={sexLabel(p.sex)} />
              <Definition k="Edad" v={patientAge(p)} />
              {!p.birth_date_is_estimated && <Definition k="Fecha de nacimiento" v={fmtDateNumeric(p.birth_date)} />}
              <Definition k="Celular" v={p.phone ? fmtPhone(p.phone) : "—"} />
              <Definition k="Correo" v={p.email ?? "—"} />
              {p.origin && <Definition k="Procedencia" v={p.origin} />}
              {p.notes && <Definition k="Notas" v={p.notes} />}
            </div>
          </Card>
          {selected ? <VisitDetail order={selected} /> : null}
        </div>
      </div>
      <Sheet open={editing} onClose={() => setEditing(false)} title="Editar paciente" wide>
        <PatientForm
          initial={patientToForm(p)}
          patientId={p.id}
          submitLabel="Guardar cambios"
          onSaved={() => {
            setEditing(false);
            toast.success("Datos actualizados.");
            void history.reload();
          }}
          onCancel={() => setEditing(false)}
        />
      </Sheet>
    </>
  );
}

function VisitDetail({ order: o }: { order: HistoryOrder }) {
  return (
    <Card
      title={
        <h2 className="h3">
          Estudios del paciente · <span className="folio">{o.folio}</span>
        </h2>
      }
      actions={
        <Link to={`/solicitudes/${o.order_id}`} className="small">
          Abrir solicitud
        </Link>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <div className="patient-facts">
          <Definition k="Fecha" v={`${fmtDateNumeric(o.occurred_at)} · ${fmtTime(o.occurred_at)}`} />
          <Definition k="Médico" v={o.doctor_display} />
          <Definition k="Sucursal" v={o.branch_name || "—"} />
          {o.origin === "migrated" && <Definition k="Sistema anterior" v={o.external_folio ? `Folio ${o.external_folio}` : "Sí"} />}
        </div>
        <Definition k="Observaciones" v={o.clinical_notes ?? "—"} />
        {o.items.length === 0 ? (
          <Empty icon="list" title="Sin estudios" hint="Esta visita no tiene estudios activos." />
        ) : (
          o.items.map((it) => <StudyResults key={it.order_item_id} item={it} />)
        )}
      </div>
    </Card>
  );
}

function StudyResults({ item: it }: { item: HistoryItem }) {
  const released = isReleased(it);
  return (
    <section className="history-study" aria-label={`Estudio: ${it.study_name}`}>
      <div className="row wrap" style={{ gap: 10 }}>
        <span className="h3 grow">
          Estudio: {it.study_name} <span className="mono muted small">{it.study_clave}</span>
        </span>
        <Chip tone={itemTone(it.state)}>{ITEM_STATE_LABEL[it.state]}</Chip>
        {it.validated_at && <span className="small faint">{fmtDateTime(it.validated_at)}</span>}
      </div>
      {!released ? (
        <p className="muted">Pendiente</p>
      ) : it.values.length === 0 ? (
        <p className="muted">Sin valores capturados.</p>
      ) : (
        <div className="table-scroll">
          <table className="table compact">
            <thead>
              <tr>
                <th>Clave</th>
                <th>Analito</th>
                <th className="right">Resultado</th>
                <th>Unidad</th>
                <th>Referencia</th>
              </tr>
            </thead>
            <tbody>
              {it.values.map((v) => (
                <tr key={v.analyte_code}>
                  <td className="mono muted">{v.analyte_code}</td>
                  <td>{v.analyte_name}</td>
                  <td className="right tnum" style={{ fontWeight: 600 }}>
                    {v.kind === "document" ? (
                      <div className="validation-doc" style={{ fontWeight: 400 }}>
                        {v.culture && <div style={{ fontWeight: 600 }}>{cultureSummary(v.culture)}</div>}
                        <RichTextView html={v.value_text} />
                      </div>
                    ) : v.value_numeric !== null ? (
                      fmtNumber(v.value_numeric, null)
                    ) : (
                      (v.value_text ?? "—")
                    )}{" "}
                    {v.flag && v.flag !== "N" ? <span className={`flag ${v.flag}`}>*{v.flag === "L" ? "B" : "A"}</span> : null}
                  </td>
                  <td className="muted">{v.unit ?? ""}</td>
                  <td className="muted small">
                    <RefText text={v.interval_text} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
